'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/** Parked position: off-screen, so the glow is never "at 0,0" before a pointer moves. */
const OFFSCREEN = '-9999px';

/**
 * The whole effect, as CSS. Exported because the test suite asserts against
 * *this string* — the one that ships — rather than restating the rules beside
 * it. (See `docs/verification-playbook.md` §12a: a rule written down twice can
 * disagree with itself.)
 *
 * Deliberately comment-free: the tests parse it to prove *which* at-rule each
 * declaration sits inside, and a brace or semicolon inside a comment would fool
 * that parser. The reasoning lives in the JSDoc on `ReadingSpotlight` below.
 */
export const SPOTLIGHT_CSS = `
.reading-spotlight {
  position: relative;
  isolation: isolate;
  --mx: ${OFFSCREEN};
  --my: ${OFFSCREEN};
  --rs-lit: 0;
}

.reading-spotlight__glow {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  border-radius: inherit;
  opacity: var(--rs-lit, 0);
  background: radial-gradient(
    220px circle at var(--mx) var(--my),
    color-mix(in srgb, var(--color-gold-500) 20%, transparent) 0%,
    color-mix(in srgb, var(--color-gold-500) 8%, transparent) 45%,
    transparent 100%
  );
}

@media (hover: hover) and (pointer: fine) {
  .reading-spotlight:hover {
    --rs-lit: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reading-spotlight,
  .reading-spotlight:hover {
    --rs-lit: 0;
  }
}
`;

/**
 * A pointer-following radial highlight over dense numeric rows, so somebody
 * parsing a table of money holds their place.
 *
 * **It is a reading aid, and it is an enhancement — never a requirement.** Every
 * row is fully legible with the effect absent, and the effect is absent for most
 * people most of the time: keyboard users never produce a pointer event, touch
 * users are filtered out in JS *and* by `@media (hover: hover) and (pointer:
 * fine)`, and anyone on `prefers-reduced-motion: reduce` is refused in both
 * layers as well. Nothing about a row's content or contrast comes from here.
 * `ReadingSpotlight.test.tsx` binds that: it renders both adopting surfaces with
 * the spotlight inert and asserts every value is still there, and it resolves
 * every row's colour token out of `globals.css` and checks it clears WCAG AA on
 * its own background — with no glow behind it.
 *
 * **Bronze, because these rows are money.** `--color-gold-500` is the design
 * system's money colour; it is referenced, not re-typed, and it falls off to
 * nothing by 220px so the highlight reads as a place-holder rather than a
 * selection.
 *
 * **Why the pointer handler writes CSS custom properties instead of React
 * state.** `pointermove` fires at the pointing device's sampling rate — 60–1000
 * events/second on a modern mouse. A `setState` per event schedules a React
 * render per event over a table that can be a hundred cells; the reconciler,
 * not the paint, becomes the cost, and the rows judder while you read them.
 * Writing `--mx`/`--my` straight onto the host element skips React entirely: the
 * only thing those two properties feed is the `background` of one absolutely
 * positioned, `pointer-events: none` layer, so the browser repaints that layer
 * and touches nothing else. No layout is invalidated, nothing animates, nothing
 * reflows — the only change is where the eye is drawn. The test asserts exactly
 * that: 40 pointer events move `--mx` and cause **zero** re-renders.
 *
 * The one measurement it needs — the host's rect — is cached and invalidated on
 * scroll/resize/pointerenter rather than read per event, because reading layout
 * immediately after writing a custom property is what would force the reflow
 * this component promises not to cause.
 *
 * The glow sits at `z-index: -1` inside an `isolation: isolate` host, so it
 * paints above the card behind it and strictly *behind* every row: it cannot
 * occlude a number even if its alpha were wrong. It is `aria-hidden` and holds
 * no text, so nothing is lost by not seeing it.
 *
 * The host must not itself be the scrolling element — coordinates come from
 * `getBoundingClientRect`, which already accounts for an *ancestor's* scroll.
 * The compare table therefore wraps an `inline-block` host inside its own
 * `overflow-x-auto` container.
 */
export function ReadingSpotlight({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  /** Cached host rect. `null` means "stale, measure once on the next event". */
  const rectRef = useRef<DOMRect | null>(null);
  /** `undefined` = not looked up yet; `null` = this environment has no matchMedia. */
  const mqlRef = useRef<MediaQueryList | null | undefined>(undefined);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const prefersReducedMotion = () => {
      if (mqlRef.current === undefined) {
        mqlRef.current =
          typeof window.matchMedia === 'function'
            ? window.matchMedia('(prefers-reduced-motion: reduce)')
            : null;
      }
      // Read `.matches` live: a MediaQueryList stays current, so someone who
      // turns reduced motion on mid-session is obeyed without a re-render.
      return mqlRef.current?.matches ?? false;
    };

    const park = () => {
      el.style.setProperty('--mx', OFFSCREEN);
      el.style.setProperty('--my', OFFSCREEN);
    };

    const onMove = (e: PointerEvent) => {
      // A touch "pointer" is the finger already covering the row. A spotlight
      // that follows it lights the one thing the hand is hiding.
      if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      if (prefersReducedMotion()) return;

      let rect = rectRef.current;
      if (!rect) {
        rect = el.getBoundingClientRect();
        rectRef.current = rect;
      }

      // NOT setState. See the JSDoc above: this runs per pointer sample, and
      // the two properties it writes feed nothing but one layer's background.
      el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
      el.style.setProperty('--my', `${e.clientY - rect.top}px`);
    };

    const invalidateRect = () => {
      rectRef.current = null;
    };

    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerenter', invalidateRect, { passive: true });
    el.addEventListener('pointerleave', park, { passive: true });
    // Capture, so an ancestor scroll container (the compare table's own
    // horizontal scroller) invalidates the cached rect too.
    window.addEventListener('scroll', invalidateRect, { passive: true, capture: true });
    window.addEventListener('resize', invalidateRect, { passive: true });

    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerenter', invalidateRect);
      el.removeEventListener('pointerleave', park);
      window.removeEventListener('scroll', invalidateRect, true);
      window.removeEventListener('resize', invalidateRect);
    };
  }, []);

  return (
    <div ref={hostRef} className={cn('reading-spotlight', className)}>
      {/* React 19 hoists and de-dupes this by `href`, so both surfaces on a page
          share one copy. */}
      <style href="reading-spotlight" precedence="default">
        {SPOTLIGHT_CSS}
      </style>
      <span aria-hidden="true" className="reading-spotlight__glow" />
      {children}
    </div>
  );
}
